import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber, Popconfirm, message, Space, Tag, Divider } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useWasteLicences, useWasteLicenceCreate, useWasteLicenceUpdate, useWasteLicenceDelete, useWasteTransfers, useWasteTransferCreate, useWasteTransferDelete } from "../../../../api-hooks/compliance";

export default function WasteTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [licModal, setLicModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [xferModal, setXferModal] = useState(false);
  const [xferPage, setXferPage] = useState(1);

  const { data: licences = [], isLoading: lLoading } = useWasteLicences(cid);
  const { data: xferRes, isLoading: xLoading } = useWasteTransfers(cid, { page: xferPage, limit: 20 });
  const transfers = (xferRes as any)?.data ?? [];
  const xferTotal = (xferRes as any)?.total ?? 0;

  const cb = (msg: string, closeFn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); closeFn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const lCreate = useWasteLicenceCreate(cid, cb("Licence added.", () => setLicModal({ open: false, rec: null })));
  const lUpdate = useWasteLicenceUpdate(cid, cb("Updated.", () => setLicModal({ open: false, rec: null })));
  const lDelete = useWasteLicenceDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const xCreate = useWasteTransferCreate(cid, cb("Transfer note created.", () => setXferModal(false)));
  const xDelete = useWasteTransferDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const licCols = [
    { title: "Licence #", dataIndex: "licence_number", key: "ln" },
    { title: "Type", dataIndex: "carrier_type", key: "ct", render: (v: string) => v?.replace(/_/g, " ").toUpperCase() },
    { title: "Issued", dataIndex: "issued_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => {
      if (!v) return "—";
      const exp = dayjs(v).isBefore(dayjs());
      return <Tag color={exp ? "red" : "green"}>{dayjs(v).format("DD/MM/YY")}{exp ? " EXPIRED" : ""}</Tag>;
    }},
    { title: "Active", dataIndex: "is_active", key: "a", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setLicModal({ open: true, rec: r }); form.setFieldsValue({ ...r, issued_date: r.issued_date ? dayjs(r.issued_date) : null, expiry_date: r.expiry_date ? dayjs(r.expiry_date) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => lDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const xferCols = [
    { title: "Consignee", dataIndex: "consignee_name", key: "c" },
    { title: "Transfer Date", dataIndex: "transfer_date", key: "td", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Waste", dataIndex: "waste_description", key: "w", render: (v: string) => <span className="text-xs truncate max-w-[150px] block">{v}</span> },
    { title: "Qty", dataIndex: "quantity", key: "q", render: (v: number, r: any) => v ? `${v} ${r.quantity_unit}` : "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => xDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm> },
  ];

  return (
    <div>
      {ctx}
      {/* Waste Carrier Licences */}
      <div className="flex justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm">Waste Carrier Licences</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setLicModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ carrier_type: "upper_tier" }); }} style={{ background: "#4c1d95" }}>Add Licence</Button>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-100 mb-5">
        <Table rowKey="id" dataSource={licences as any[]} columns={licCols} loading={lLoading} size="small" pagination={false} />
      </div>

      <Divider plain className="my-3">Waste Transfer Notes</Divider>
      <div className="flex justify-end mb-2">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setXferModal(true); form.resetFields(); form.setFieldsValue({ quantity_unit: "kg" }); }} style={{ background: "#4c1d95" }}>Add Transfer Note</Button>
      </div>
      <Table rowKey="id" dataSource={transfers} columns={xferCols} loading={xLoading} size="small" pagination={{ current: xferPage, pageSize: 20, total: xferTotal, onChange: setXferPage }} />

      {/* Licence Modal */}
      <Modal open={licModal.open} onCancel={() => setLicModal({ open: false, rec: null })} footer={null} destroyOnClose title={licModal.rec ? "Edit Waste Carrier Licence" : "New Waste Carrier Licence"} width={420}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, issued_date: vals.issued_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD") };
          if (licModal.rec) lUpdate.mutate({ id: licModal.rec.id, ...p }); else lCreate.mutate(p);
        }}>
          <Form.Item name="licence_number" label="Licence Number" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="carrier_type" label="Carrier Type" rules={[{ required: true }]}><Select options={["upper_tier","lower_tier","exempt"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="issued_date" label="Issued Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={lCreate.isPending || lUpdate.isPending}>Save</Button>
            <Button onClick={() => setLicModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Transfer Note Modal */}
      <Modal open={xferModal} onCancel={() => setXferModal(false)} footer={null} destroyOnClose title="New Waste Transfer Note" width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          xCreate.mutate({ ...vals, transfer_date: vals.transfer_date?.format("YYYY-MM-DD") });
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="consignee_name" label="Consignee Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="transfer_date" label="Transfer Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="consignee_address" label="Consignee Address" rules={[{ required: true }]}><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="consignee_licence" label="Consignee Licence #"><Input /></Form.Item>
          <Form.Item name="waste_description" label="Waste Description" rules={[{ required: true }]}><Input.TextArea rows={2} /></Form.Item>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="quantity" label="Quantity"><InputNumber className="w-full" min={0} /></Form.Item>
            <Form.Item name="quantity_unit" label="Unit"><Select options={["kg","tonnes","litres","m3"].map(v => ({ value: v, label: v }))} /></Form.Item>
            <Form.Item name="created_by" label="Created By (User ID)" rules={[{ required: true }]}><Input type="number" /></Form.Item>
          </div>
          <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={xCreate.isPending}>Save</Button>
            <Button onClick={() => setXferModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
